import { useEffect, useRef, useState } from 'react';
import { sendVerificationEmail } from '../services/accountSecurity';

/** A resend waits this long after each send (the backend rate-limits it). */
export const VERIFICATION_RESEND_COOLDOWN_SECONDS = 60;

/** Sends one email to an address; resolves an error to show on failure. */
export type EmailLinkSender = (
  email: string
) => Promise<{ success: boolean; error?: string }>;

/**
 * Sends an emailed link (verification, password reset) and tracks the "Check
 * your inbox" state: once sent, Resend stays disabled for a minute, counting
 * down.
 */
export const useEmailLinkSender = (sendEmail: EmailLinkSender) => {
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearInterval(timer.current), []);

  const startCooldown = () => {
    window.clearInterval(timer.current);
    setCooldownSeconds(VERIFICATION_RESEND_COOLDOWN_SECONDS);

    timer.current = window.setInterval(() => {
      setCooldownSeconds((seconds) => {
        if (seconds <= 1) {
          window.clearInterval(timer.current);
          return 0;
        }
        return seconds - 1;
      });
    }, 1000);
  };

  const send = async (email: string) => {
    if (isSending || cooldownSeconds > 0) return;
    setIsSending(true);
    setError(null);
    try {
      const result = await sendEmail(email);
      if (!result.success) {
        setError(result.error ?? null);
        return;
      }
      setIsSent(true);
      startCooldown();
    } finally {
      setIsSending(false);
    }
  };

  return { send, isSending, isSent, error, cooldownSeconds };
};

/** `useEmailLinkSender` for the primary email's verification link. */
export const useVerificationEmail = (callbackPath: string) =>
  useEmailLinkSender((email) => sendVerificationEmail(email, callbackPath));
